import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import ScaleIcon from "@mui/icons-material/Scale";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CategoryIcon from "@mui/icons-material/Category";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink, useParams } from "react-router";
import { useEquipment } from "../hooks/useApiQueries.js";
import { ContentCard } from "../components/ContentCard.js";
import { SEO } from "../components/SEO.js";
import { MachineHero } from "../components/MachineHero.js";
import { SpecTileGrid, type SpecTileProps } from "../components/SpecTile.js";
import { NeonEyebrow, NeonDivider, neonButtonSx } from "../components/Neon.js";
import { formatKg } from "../lib/displayHelpers.js";

export function EquipmentDetail() {
  const { slug = "" } = useParams();
  const { data, isLoading } = useEquipment(slug);
  if (isLoading)
    return (
      <>
        <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="text" width="60%" height={48} />
      </>
    );
  if (!data)
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <NeonEyebrow glow>EQUIPMENT</NeonEyebrow>
        <Typography variant="h2" sx={{ fontSize: 36, mt: 2, mb: 2, fontWeight: 900 }}>
          Not in our books
        </Typography>
        <Alert severity="info" sx={{ maxWidth: 480, mx: "auto", textAlign: "left" }}>
          We don't have this machine yet — it isn't in Tractrs's catalogue or hasn't been
          tagged in any approved content. Try browsing all content instead.
        </Alert>
        <Button
          component={RouterLink}
          to="/content"
          variant="contained"
          color="primary"
          sx={{ mt: 3, ...neonButtonSx }}
        >
          Browse content
        </Button>
      </Box>
    );

  const m = data.machinery;
  const specs: SpecTileProps[] = [
    m.enginePowerHp != null && {
      icon: <BoltIcon />,
      label: "Engine power",
      value: `${m.enginePowerHp} hp`,
      accent: true,
    },
    m.operatingWeightKg != null && {
      icon: <ScaleIcon />,
      label: "Operating weight",
      value: formatKg(m.operatingWeightKg) ?? "—",
    },
    m.year != null && {
      icon: <CalendarMonthIcon />,
      label: "Model year",
      value: String(m.year),
    },
    m.category && {
      icon: <CategoryIcon />,
      label: "Category",
      value: m.category.name,
    },
  ].filter(Boolean) as SpecTileProps[];

  const heroChips = [
    m.brand && { label: m.brand.name, color: "primary" as const },
    m.category && { label: m.category.name, color: "default" as const },
    m.year != null && { label: String(m.year), color: "default" as const },
  ].filter(Boolean) as Array<{ label: string; color: "default" | "primary" | "white" }>;

  return (
    <>
      <SEO
        title={m.name}
        description={
          m.description ??
          `${m.brand?.name ?? ""} ${m.name} — videos, articles and operator stories on Corian Park`.trim()
        }
        canonical={`https://corianpark.au/equipment/${m.slug}`}
        ogImage={m.primaryImageUrl ?? undefined}
      />

      {/* Bleed-escape hero */}
      <Box sx={{ mx: { xs: -2, sm: -3 }, mt: -4, mb: { xs: 3, md: 5 } }}>
        <MachineHero
          imageUrl={m.primaryImageUrl}
          eyebrow="EQUIPMENT"
          brand={m.brand?.name ?? null}
          title={m.name}
          subtitle={m.category?.name ?? null}
          chips={heroChips}
        />
      </Box>

      {specs.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <SpecTileGrid tiles={specs} />
        </Box>
      )}

      {m.description && (
        <Box sx={{ mb: 5 }}>
          <NeonEyebrow>ABOUT</NeonEyebrow>
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, mt: 1, mb: 2 }}
          >
            What sets this machine apart
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 16, lineHeight: 1.7, color: "text.secondary" }}>
            {m.description}
          </Typography>
        </Box>
      )}

      <NeonDivider sx={{ mb: 4 }} />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ alignItems: { sm: "flex-end" }, justifyContent: "space-between", mb: 3 }}
      >
        <Box>
          <NeonEyebrow>FEATURED IN</NeonEyebrow>
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, mt: 1, lineHeight: 1.1 }}
          >
            Where the {m.name} shows up
          </Typography>
        </Box>
        <Button
          component="a"
          href={`https://tractrs.com/machinery/${m.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
          sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
        >
          View on Tractrs
        </Button>
      </Stack>

      {data.content.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          No videos featuring this machine yet. Check back as new content lands.
        </Alert>
      ) : (
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {data.content.map((c) => (
            <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <ContentCard item={c} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Quote CTA */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 1,
          background:
            "linear-gradient(135deg, rgba(255,176,0,0.12) 0%, rgba(255,136,0,0.04) 70%, transparent 100%)",
          border: "1px solid",
          borderColor: "primary.main",
          boxShadow: "0 0 30px -10px rgba(255,176,0,0.5)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Need work done by an operator running a {m.name}?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              The Corian Park team routes earthworks quotes to operators with the right gear in your area.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={m.brand?.name ?? "Brand"} size="small" sx={{ bgcolor: "background.paper" }} />
            {m.category && <Chip label={m.category.name} size="small" variant="outlined" />}
          </Stack>
          <Button
            component={RouterLink}
            to="/quote"
            variant="contained"
            color="primary"
            size="large"
            startIcon={<RequestQuoteIcon />}
            sx={{ ...neonButtonSx, flexShrink: 0 }}
          >
            Get a quote
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ my: 4 }} />
    </>
  );
}
